import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import type { Article } from '@/data/articles';
import ScrollReveal from './ScrollReveal';

const LatestArticles = () => {
  // The article archive is large: load it only when this section approaches the viewport.
  const sectionRef = useRef<HTMLElement>(null);
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [formatDate, setFormatDate] = useState<(d: string) => string>(() => (d: string) => d);

  useEffect(() => {
    let active = true;
    const load = () => {
      import('@/data/articles').then((mod) => {
        if (!active) return;
        setLatestArticles(
          [...mod.articles]
            .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
            .slice(0, 3)
        );
        setFormatDate(() => mod.formatDate);
      });
    };

    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      load();
      return () => {
        active = false;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(el);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="section-padding relative overflow-hidden">

      <div className="container-premium">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <ScrollReveal>
              <Badge className="bg-gold/20 text-gold hover:bg-gold/30 mb-4">
                Blog
              </Badge>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl">
                Ultimi Insights
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-muted-foreground mt-4 max-w-xl">
                Strategie, guide pratiche e novità sul marketing digitale per far crescere il tuo business.
              </p>
            </ScrollReveal>
          </div>
          <ScrollReveal delay={0.3}>
            <Link 
              to="/insights" 
              className="group inline-flex items-center gap-2 text-gold font-medium hover:gap-3 transition-all"
            >
              Vedi tutti gli articoli
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {latestArticles.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <Link
                to={`/insights/${article.slug}`}
                className="block glass-card-hover overflow-hidden h-full"
                aria-label={`Leggi l'articolo: ${article.title}`}
              >
                {/* Image */}
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={article.heroImage}
                    alt={article.heroImageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                  
                  {/* Category Badge */}
                  <Badge 
                    className="absolute top-4 left-4 bg-gold/90 text-navy-deep hover:bg-gold"
                  >
                    {article.categoryLabel}
                  </Badge>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <time dateTime={article.publishedAt}>
                      {formatDate(article.publishedAt)}
                    </time>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {article.readingTime} min
                    </span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="font-serif text-xl text-foreground mb-3 line-clamp-2 group-hover:text-gold transition-colors">
                    {article.title}
                  </h3>
                  
                  {/* Excerpt */}
                  <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  
                  {/* Read more */}
                  <span className="inline-flex items-center gap-2 text-gold font-medium text-sm group-hover:gap-3 transition-all">
                    Leggi articolo
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LatestArticles;
